import { Icon } from 'antd';
import React, { memo, useCallback, useLayoutEffect, useRef } from 'react';

import RoundedButton from '../../../../../../../../components/RoundedButton/RoundedButton';
import { UrgentIcon } from '../../../../../../../../resource/icons';
import BaseCheckbox from '../../../../../../../RoomsAcceptance/shared/ui/BaseCheckbox';
import Item from '../../../../../../../RoomsAcceptance/shared/ui/Item';
import styles from './ServiceSetsSection.module.scss';
import { hasSameServiceSelection, isImmediateReason, noop } from './utils';

const ServiceReasonItem = memo(
  ({ reason, isSelected, isEditable, onReasonToggle }) => {
    const handleClick = useCallback(() => {
      onReasonToggle(reason.value);
    }, [onReasonToggle, reason.value]);

    const handleActionClick = useCallback(
      (event) => {
        event.stopPropagation();
        onReasonToggle(reason.value);
      },
      [onReasonToggle, reason.value]
    );

    return (
      <Item.Memoized
        title={reason.text || reason.reasonId}
        action={
          <span
            className={styles.serviceReasonAction}
            onClick={handleActionClick}
          >
            {isImmediateReason(reason) && (
              <span
                className={styles.urgentIconWrapper}
                role="img"
                aria-label="Аварийная"
                title="Аварийная"
              >
                <UrgentIcon className={styles.urgentIcon} />
              </span>
            )}
            <BaseCheckbox checked={isSelected} onChange={noop} />
          </span>
        }
        isActive={isSelected}
        disabled={!isEditable}
        onClick={handleClick}
      />
    );
  }
);

const ServiceReasonGroup = memo(
  ({
    service,
    selectedReasonIdSet,
    isSelected,
    isEditable,
    isCollapsed,
    onCollapseToggle,
    onServiceToggle,
    onReasonToggle,
  }) => {
    const selectedReasonCount = service.reasonKeys.reduce(
      (count, reasonId) =>
        selectedReasonIdSet.has(String(reasonId)) ? count + 1 : count,
      0
    );
    const isServiceSelected = Boolean(
      isSelected ||
        (service.reasonKeys.length &&
          selectedReasonCount === service.reasonKeys.length)
    );
    const isServiceIndeterminate = Boolean(
      selectedReasonCount && !isServiceSelected
    );
    const handleServiceClick = useCallback(() => {
      onServiceToggle(service);
    }, [onServiceToggle, service]);

    const handleCollapseClick = useCallback(
      (event) => {
        event.stopPropagation();
        onCollapseToggle(service.value);
      },
      [onCollapseToggle, service.value]
    );

    const handleCheckboxClick = useCallback(
      (event) => {
        event.stopPropagation();
        onServiceToggle(service);
      },
      [onServiceToggle, service]
    );

    return (
      <li key={service.value} className={styles.serviceReasonGroup}>
        <div
          className={styles.serviceReasonGroupHeader}
          onClick={handleServiceClick}
        >
          <div className={styles.serviceReasonGroupTitleBlock}>
            <RoundedButton
              className={styles.serviceReasonGroupCollapseButton}
              aria-label={isCollapsed ? 'Развернуть услугу' : 'Свернуть услугу'}
              onClick={handleCollapseClick}
              text={<Icon type={isCollapsed ? 'caret-down' : 'caret-up'} />}
            />
            <div className={styles.serviceReasonGroupTitle}>
              {service.text || service.value}
            </div>
          </div>
          <div className={styles.serviceReasonGroupSelection}>
            {(isImmediateReason(service) ||
              service.reasons.some(isImmediateReason)) && (
              <span
                className={styles.urgentIconWrapper}
                role="img"
                aria-label="Аварийная"
                title={
                  isImmediateReason(service)
                    ? 'Аварийная'
                    : 'Есть аварийные причины'
                }
              >
                <UrgentIcon className={styles.urgentIcon} />
              </span>
            )}
            <span className={styles.serviceReasonGroupCounter}>
              {selectedReasonCount}/{service.reasonKeys.length}
            </span>
            <BaseCheckbox
              checked={isServiceSelected}
              disabled={
                !isEditable ||
                (isSelected === undefined && !service.reasonKeys.length)
              }
              indeterminate={isServiceIndeterminate}
              onClick={handleCheckboxClick}
              onChange={noop}
            />
          </div>
        </div>
        {!isCollapsed && (
          <ul className={styles.serviceReasonItems}>
            {service.visibleReasons.map((reason) => (
              <ServiceReasonItem
                key={reason.value}
                reason={reason}
                isSelected={selectedReasonIdSet.has(String(reason.value))}
                isEditable={isEditable}
                onReasonToggle={onReasonToggle}
              />
            ))}
          </ul>
        )}
      </li>
    );
  },
  (prevProps, nextProps) =>
    prevProps.service === nextProps.service &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.isEditable === nextProps.isEditable &&
    prevProps.isCollapsed === nextProps.isCollapsed &&
    prevProps.onCollapseToggle === nextProps.onCollapseToggle &&
    prevProps.onServiceToggle === nextProps.onServiceToggle &&
    prevProps.onReasonToggle === nextProps.onReasonToggle &&
    hasSameServiceSelection(
      prevProps.service.reasonKeys,
      prevProps.selectedReasonIdSet,
      nextProps.selectedReasonIdSet
    )
);

export const ServiceReasonList = ({
  collapsedServiceKeys,
  filteredServiceReasonGroups,
  isEditable,
  reasonsLoading,
  selectedReasonIdSet,
  selectedServiceIdSet,
  servicesLoading,
  onReasonToggle,
  onServiceCollapseToggle,
  onServiceToggle,
}) => {
  const listRef = useRef(null);
  const pendingScrollRef = useRef(null);
  const rememberScroll = useCallback(() => {
    const positions = [];
    let element = listRef.current;

    while (element) {
      positions.push({
        element,
        top: element.scrollTop,
        left: element.scrollLeft,
      });
      element = element.parentElement;
    }

    pendingScrollRef.current = positions;
  }, []);

  useLayoutEffect(() => {
    (pendingScrollRef.current || []).forEach(({ element, top, left }) => {
      element.scrollTop = top;
      element.scrollLeft = left;
    });
    pendingScrollRef.current = null;
  }, [filteredServiceReasonGroups, selectedReasonIdSet, selectedServiceIdSet]);

  const handleReasonToggle = useCallback(
    (reasonId) => {
      rememberScroll();
      onReasonToggle(reasonId);
    },
    [onReasonToggle, rememberScroll]
  );
  const handleServiceToggle = useCallback(
    (service) => {
      rememberScroll();
      onServiceToggle(service);
    },
    [onServiceToggle, rememberScroll]
  );

  return (
    <ul className={styles.serviceReasonList} ref={listRef}>
      {filteredServiceReasonGroups.length ? (
        filteredServiceReasonGroups.map((service) => (
          <ServiceReasonGroup
            key={service.value}
            service={service}
            selectedReasonIdSet={selectedReasonIdSet}
            isSelected={selectedServiceIdSet?.has(String(service.value))}
            isEditable={isEditable}
            isCollapsed={collapsedServiceKeys.has(String(service.value))}
            onCollapseToggle={onServiceCollapseToggle}
            onServiceToggle={handleServiceToggle}
            onReasonToggle={handleReasonToggle}
          />
        ))
      ) : (
        <li className={styles.workerEmpty}>
          {servicesLoading || reasonsLoading
            ? 'Загрузка услуг...'
            : 'Услуги и причины не найдены'}
        </li>
      )}
    </ul>
  );
};
